<template>
  <div class="team-form">
    <h1 class="team-form__title page-title">
      {{ isEditing ? 'Edit team' : 'Create team' }}
    </h1>

    <div class="team-header__background">
      <team-image 
        :team-id="teamId" 
        :activityId="formData.activityId"
        :alt="teamData.name" 
        :key="formData.activityId"
        :isEditing="true"
        @imageChanged="onImageChanged"/>
    </div>
    
    <div class="card">
      <el-form
        :model="formData"
        :rules="formRules"
        ref="teamForm"
        class="team-form__form"
        label-position="top"
      >
        <div class="row">
          <div class="col col-xs-12 col-md-6">
            <el-form-item prop="name" :error="errors.name" label="Team name">
              <el-input v-model="formData.name" />
            </el-form-item>
          </div>

          <div class="col col-xs-12 col-md-6">
            <el-form-item
              prop="activityId"
              :error="errors.activityId"
              label="Activity"
            >
              <el-select class="wide"
                v-model="formData.activityId"
                :disabled="isEditing">
                <el-option
                  v-for="type in descriptions.activityType"
                  :key="type.value"
                  :label="type.description"
                  :value="type.value"
                >
                </el-option>
              </el-select>
            </el-form-item>
          </div>
        </div>
        <div class="row">
          <div class="col col-xs-12">
            <el-form-item prop="link" :error="errors.link" label="Team URL">
              <el-input v-model="formData.link" />
            </el-form-item>
          </div>
        </div>

        <div class="row">
          <div class="col col-xs-12">
            <el-form-item prop="motto" :error="errors.motto" label="Team motto">
              <el-input v-model="formData.motto" />
            </el-form-item>
          </div>

          <div class="col col-xs-12">
            <el-form-item
              prop="statement"
              :error="errors.statement"
              label="Description"
            >
              <quill-editor
                class="team-form__input__quill"
                v-model:content="formData.statement"
                ref="descriptionInput"
                :options="editorOptions"
                contentType="html"
                toolbar="full"
                theme="snow"
              ></quill-editor>
              <!-- <el-input
                v-model="formData.statement"
                type="textarea"
                :rows="5"
              /> -->
            </el-form-item>
          </div>
        </div>
        <div class="row">
          <div class="col col-xs-12">
            <el-form-item prop="location" :error="errors.location" label="Location">
              <el-input v-model="formData.location" />
            </el-form-item>
          </div>
        </div>

        <!-- New fields added here -->
        <div class="row">
          <div class="col col-xs-12">
            <el-form-item prop="webSite" :error="errors.webSite" label="Web Site">
              <el-input v-model="formData.webSite" />
            </el-form-item>
          </div>

          <div class="col col-xs-12">
            <el-form-item prop="telegramChannel" :error="errors.telegramChannel" label="Telegram Channel">
              <el-input v-model="formData.telegramChannel" />
            </el-form-item>
          </div>

          <div class="col col-xs-12">
            <el-form-item prop="whatsappGroup" :error="errors.whatsappGroup" label="WhatsApp Group">
              <el-input v-model="formData.whatsappGroup" />
            </el-form-item>
          </div>

          <div class="col col-xs-12">
            <el-form-item prop="galleryDriveShare" :error="errors.galleryDriveShare" label="Gallery Google Drive Share">
              <el-input v-model="formData.galleryDriveShare" />
            </el-form-item>
          </div>
        </div>
        <!-- End of new fields -->

        <div class="team-form__subtitle" v-if="!isEditing">
          Links to social networks
        </div>

        <div class="row" v-if="!isEditing">
          <div
            class="col col-xs-12"
            v-for="(social, index) in socialArray"
            :key="index"
          >
            <el-form label-position="top">
              <div class="row">
                <div class="col col-xs-11 col-md-4">
                  <el-form-item :error="social.networkIdError">
                    <el-select
                      class="wide"
                      v-model="social.socialNetworkId"
                      placeholder="Choose a social network"
                    >
                      <el-option
                        v-for="socialItem in socialDescriptions"
                        :key="socialItem.value"
                        :label="socialItem.description"
                        :value="socialItem.value"
                      >
                      </el-option>
                    </el-select>
                  </el-form-item>
                </div>

                <div
                  class="col col-xs-1 col-md-1 col-md-last col-lg-last col-xl-last col-xxl-last"
                >
                  <el-button
                    class="personal-data__remove-social-button"
                    @click="removeSocial(social)"
                  >
                  <el-icon><Delete/></el-icon>
                  </el-button>
                </div>

                <div class="col col-xs-11 col-md-7">
                  <el-form-item :error="social.accountError">
                    <el-input
                      v-model="social.accountLink"
                      placeholder="Link to team page in social network"
                    ></el-input>
                  </el-form-item>
                </div>
              </div>
            </el-form>
          </div>

          <div class="col col-xs-12">
            <el-button @click="addSocial">
              <el-icon><Plus/></el-icon>
              Add social network
            </el-button>
          </div>
        </div>
      </el-form>
    </div>

    <div class="team-form__buttons">
      <el-button type="primary" @click="handleReset">
        {{ isEditing ? 'Cancel' : 'Reset' }}
      </el-button>
      <el-button
        type="primary"
        @click="validateForm"
        :disabled="isButtonDisabled('save-team')"
      >
        {{ isEditing ? 'Save' : 'Create' }}
      </el-button>
    </div>

    <edit-team-socials
      v-if="isEditing"
      :social-networks="oldData ? oldData.teamSocialNetworks : []"
      :team-id="teamId"
      @update-data="fetchTeamData"
      ref="teamSocials"
    />
  </div>
</template>

<script>
import EditTeamSocials from '@/components/teams/EditTeamSocials.vue';
import ConfirmationDialog from '@/components/common/ConfirmationDialog.vue';
import TeamImage from './TeamImage.vue';

import { getTeam, addNewTeam, updateTeam } from '@/api/team.js';
import { getSocialsTypeDescriptions } from '@/api/profile.js';
import {
  parseErrorsByFields,
  removeArrayElementById,
  removeTimezoneOffset,
  isTwoObjectsEqual,
} from '@/utils/index.js';
import { showMessage } from '@/utils/messages.js';
import { disableButton } from '@/mixins/disableButton.js';
import { uploadImage } from '@/api/images.js';
import { QuillEditor } from '@vueup/vue-quill';

export default {
  name: 'TeamForm',
  mixins: [disableButton],
  components: {
    EditTeamSocials,
    ConfirmationDialog,
    TeamImage,
    QuillEditor,
  },
  data() {
    return {
      editorOptions: {
        theme: 'snow',
        placeholder: 'Enter team description',
      },
      showCheckLocationBox: false,
      oldData: null,
      socialArray: [],
      socialDescriptions: [],
      formData: {
        activityId: null,
        name: null,
        motto: null,
        statement: null,
        location: null,
        webSite: null,
        telegramChannel: null,
        whatsappGroup: null,
        galleryDriveShare: null,
      },
      errors: {
        activityId: null,
        name: null,
        link: null,
        motto: null,
        statement: null,
        location: null,
        webSite: null,
        telegramChannel: null,
        whatsappGroup: null,
        galleryDriveShare: null,
      },
      formRules: {
        name: [
          {
            required: true,
            trigger: 'input',
            message: 'Please enter team name',
          },
        ],
        activityId: [
          {
            required: true,
            trigger: 'change',
            message: 'Please select an activity',
          },
        ],
        link: [{ required: false }],
        motto: [
          {
            required: false,
            trigger: 'input',
            message: 'Please enter team motto',
          },
        ],
        statement: [
          {
            required: false,
            trigger: 'input',
            message: 'Please enter team statement',
          },
        ],
        location: [
          {
            required: false,
            trigger: 'input',
            message: 'Please enter team location',
          },
        ],
        webSite: [
          {
            required: false,
            trigger: 'input',
            message: 'Please enter team website',
          },
        ],
        telegramChannel: [
          {
            required: false,
            trigger: 'input',
            message: 'Please enter team telegram channel',
          },
        ],
        whatsappGroup: [
          {
            required: false,
            trigger: 'input',
            message: 'Please enter team whatsapp group',
          },
        ],
        galleryDriveShare: [
          {
            required: false,
            trigger: 'input',
            message: 'Please enter team gallery drive share',
          },
        ],
      },
    };
  },
  computed: {
    isEditing() {
      return Boolean(this.$route.query.edit) || false;
    },
    teamId() {
      return Number(this.$route.query.teamId) || null;
    },
    teamData() {
      const { formData } = this;

      const data = {
        activityId: formData.activityId,
        name: formData.name,
        link: formData.link,
        motto: formData.motto,
        statement: formData.statement,
        location: formData.location,
        webSite: formData.webSite,
        telegramChannel: formData.telegramChannel,
        whatsappGroup: formData.whatsappGroup,
        galleryDriveShare: formData.galleryDriveShare,
        teamSocialNetworks: null,
      };

      if (this.socialArray.length > 0) {
        data.teamSocialNetworks = this.socialArray.map((social) => {
          let accountLink = social.accountLink;
          if (social.accountLink.includes('/')) {
            const accounLinkSplited = social.accountLink.split('/');
            accountLink = accounLinkSplited[accounLinkSplited.length - 1];
          }
          return {
            accountLink,
            socialNetworkId: social.socialNetworkId,
          };
        });
      }

      return data;
    },
    descriptions() {
      return this.$store.state.descriptions;
    },
    isFormFilled() {
      const data = {
        name: this.formData.name,
        activityId: this.formData.activityId,
      };
      return Object.values(data).every((value) => value !== null);
    },
    isSocialsFilled() {
      const { socialArray } = this;

      return socialArray.every(
        (social) =>
          social.accountLink !== null && social.socialNetworkId !== null,
      );
    },
  },
  methods: {
    showMessage,
    parseErrorsByFields,
    removeArrayElementById,
    removeTimezoneOffset,
    isTwoObjectsEqual,
    async fetchTeamData() {
      const { teamId } = this;
      const { data, success } = await getTeam({ teamId });

      if (success) {
        this.oldData = data;
        this.oldData.link = data.link == teamId ? null : data.link;
        this.setTeamData();
      }
    },
    async requestAddTeam() {
      this.disableButton('save-team');
      const { teamData } = this;
      const { data, success, errors } = await addNewTeam(teamData);
      this.enableButton('save-team');

      if (success) {
        this.showMessage({
          type: 'success',
          message: 'Team has been created',
        });
        this.$router.push({
          name: 'TeamPage',
          params: {
            link: data.link,
          },
        });
      } else if (errors) {
        this.errors = { ...this.errors, ...parseErrorsByFields(errors) };
        if (
          errors.some((error) => error.propertyName.includes('SocialNetworks'))
        ) {
          this.setSocialErrors(errors);
        }
      }
    },
    async requestUpdateTeam() {
      this.disableButton('save-team');
      const { teamData, teamId } = this;
      const { success, errors } = await updateTeam({
        teamId,
        name: teamData.name,
        motto: teamData.motto,
        link: teamData.link,
        statement: teamData.statement,
        location: teamData.location,
        website: teamData.webSite,
        telegramChannel: teamData.telegramChannel,
        whatsappGroup: teamData.whatsappGroup,
        galleryDriveShare: teamData.galleryDriveShare,
      });
      this.enableButton('save-team');

      if (success) {
        this.showMessage({
          type: 'success',
          message: 'Team has been updated',
        });

        if( this.selectedFile != null) {
          await this.uploadTeamImage();
        }

        this.$router.push({
          name: 'TeamPage',
          params: {
            link: teamData.link || teamId,
          },
        });
      } else if (errors) {
        this.errors = { ...this.errors, ...parseErrorsByFields(errors) };
      }
    },

    async uploadTeamImage() {
      try {
        // Use FormData for multipart data
        const formData = new FormData();

        const { teamId, selectedFile } = this;

        formData.append('entityId', teamId);
        formData.append('imageType', selectedFile.type);
        formData.append('image', selectedFile);

        //console.log(formData.get('imageType'));

        const response = await uploadImage(formData, '/api/Team/UploadTeamPicture');

        this.selectedFile = null;

        // Handle successful upload
        console.log("Image uploaded successfully:", response.data);
      } catch (err) {
        console.error('Error uploading image:', err);
        this.error = "Failed to upload image. Please try again.";
      }
    },

    async fetchSocialDescriptions() {
      const { data, success } = await getSocialsTypeDescriptions('Team');

      if (success) {
        this.socialDescriptions = data.items;
      }
    },
    removeSocial(social) {
      const { socialArray, socialNetworks } = this;
      const socialIndex = socialArray.indexOf(social);
      this.socialArray = this.removeArrayElementById(socialArray, socialIndex);
    },
    clearSocialErrors() {
      this.socialArray.forEach((social) => {
        social.accountError = null;
        social.networkIdError = null;
      });
    },
    addSocial() {
      this.socialArray.push({
        accountLink: null,
        accountError: null,
        networkIdError: null,
        socialNetworkId: null,
      });
    },

    onImageChanged(selectedFile) {
      this.selectedFile = selectedFile;
    },

    validateForm() {
      const {
        isFormFilled,
        isEditing,
        isSocialsFilled,
        formData,
        oldData,
      } = this;

      if (this.$refs.teamForm) {
        this.$refs.teamForm.validate();
      }

      console.log("formData:", formData);

      if (isEditing) {
        // сравнить старые данные и новые
        // если есть изменения, обновить

        const oldFormData = {
          name: oldData.name,
          motto: oldData.motto,
          link: oldData.link,
          statement: oldData.statement,
          location: oldData.location,
          webSite: oldData.webSite,
          telegramChannel: oldData.telegramChannel,
          whatsappGroup: oldData.whatsappGroup,
          galleryDriveShare: oldData.galleryDriveShare,
          activityId: this.descriptions.activityType.find(
            (type) => type.description === oldData.activityName,
          ).value,
        };

        if (this.isTwoObjectsEqual(oldFormData, formData) && this.selectedFile == null) {
          this.showMessage({
            type: 'error',
            message: 'There are no changes to be saved',
          });
        } else {
          this.requestUpdateTeam();
        }
      } else if (isFormFilled && isSocialsFilled) {
        this.requestAddTeam();
      }
    },
    clearForm() {
      const { isEditing } = this;

      if (isEditing) {
        this.setTeamData();
      } else {
        for (const key in this.formData) {
          this.formData[key] = null;
        }
      }
    },
    async setTeamData() {
      const { oldData } = this;

      if (oldData) {
        this.formData.activityId = this.descriptions.activityType.find(
          (type) => type.description === oldData.activityName,
        ).value;

        this.formData = {
          ...this.formData,
          name: oldData.name,
          motto: oldData.motto,
          link: oldData.link,
          statement: oldData.statement,
          location: oldData.location,
          webSite: oldData.webSite,
          telegramChannel: oldData.telegramChannel,
          whatsappGroup: oldData.whatsappGroup,
          galleryDriveShare: oldData.galleryDriveShare,
        };
      }
    },
    setSocialErrors(errors) {
      errors.forEach((error) => {
        if (error.propertyName.includes('EventSocialNetworks')) {
          const fieldName = error.propertyName.includes('AccountLink')
            ? 'accountError'
            : 'networkIdError';
          const socialIndex = error.propertyName.match(/\d+/);
          const message = error.errorMessages[0];

          this.socialArray[socialIndex][fieldName] = message;
        }
      });
    },
    handleReset() {
      const { isEditing, oldData, teamId } = this;
      if (isEditing) {
        this.$router.push({
          name: 'TeamPage',
          params: {
            link: oldData.link || teamId,
          },
        });
      } else {
        this.clearForm();
      }
    },
  },
  created() {
    this.fetchSocialDescriptions();

    const { isEditing } = this;

    if (isEditing) {
      this.fetchTeamData();
    }
  },
};
</script>

<style lang="scss">
.team-form {
  $this: &;

  &__title {
    margin: 0;
    margin-bottom: var(--global-dimension-size-250);

    font-weight: normal;
    font-size: var(--font-size-800);
    line-height: 1.6;
  }
  &__form {
  }

  #{$this}__input {
      display: block;
      margin-top: var(--global-dimension-size-125);
      cursor: unset;
      flex-wrap: wrap;
      align-items: flex-start;
      &__text {
        width: 100%;
        height: 100px;
      }
      &__quill {
        width: 100%;
        height: 400px;
      }
    }

  &__subtitle {
    margin-top: var(--global-dimension-size-250);
    margin-bottom: var(--global-dimension-size-200);
    font-size: var(--font-size-200);
    line-height: 1.6;
  }
  &__buttons {
    margin-top: var(--global-dimension-size-350);
    margin-bottom: var(--global-dimension-size-350);
    text-align: right;
  }
}
</style>
